import React, {useState, useCallback, useMemo} from 'react';
import {List} from 'immutable';
import {useTracking} from 'react-tracking';
import type {ClassroomModelV1, TeacherModelV1, SubjectModelV2} from '@albert-io/models';
import {resource} from '@albert-io/json-api-framework/request/builder';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import appStore from 'client/AppStore';
import classnames from 'classnames';
import {
  addToast,
  Anchor,
  Banner,
  Card,
  Heading,
  DropdownItem,
  Icon,
  MenuListItem,
  MobileDropdown,
  NoDataMsg,
  Text,
  WithTooltip,
  Button
} from '@albert-io/atomic';
import {setFreePracticeRestrictionForClassroom} from 'client/generic/SubjectMenuRenderer/helpers/queries';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import EmptyState from '../EmptyState/index.react';

import RestrictFreePracticeModal from './RestrictFreePracticeModal.react';
import {SubjectsListCard} from './SubjectsListCard.react';
import './teacher-subjects-list.scss';

const TrackedEmptyState = ({title, body}: {title: string; body: string}) => {
  const {Track} = useTracking({feature: 'Subjects'});
  return (
    <Track>
      <EmptyState title={title} body={body} />
    </Track>
  );
};

const getClassroomQuery = (classId: string) => {
  return resource('classroom_v1')
    .mandarkEndpoint(['classrooms_v1', classId])
    .include('subjects_v2')
    .include('subjects_v2.curriculum_area_v1')
    .include('subjects_v2.guides_v1')
    .withMeta('subject_v2,classroom_v1')
    .customQuery({
      meta: {
        context: {
          classroom: {
            licensed_subjects: true
          }
        }
      }
    });
};

const getTeacherQuery = () => {
  return resource('teacher_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState()])
    .include('school_v5')
    .withMeta('teacher_v1')
    .customQuery({
      meta: {
        context: {
          teacher: {
            license_info: true
          }
        }
      }
    });
};

const EmptySubjectsList = () => (
  <NoDataMsg.Container centered className='teacher-subjects-list--empty'>
    <NoDataMsg.Body>
      Students can only practice on questions you&apos;ve assigned to them. If you want to allow
      students to answer any question in a subject,&nbsp;
      <Anchor to='/subjects/high-school/test-prep'>visit the Library tab</Anchor>
      &nbsp;and toggle on access.
    </NoDataMsg.Body>
  </NoDataMsg.Container>
);

interface SubjectsListProps {
  teacher: TeacherModelV1;
  classroom: ClassroomModelV1;
  subjects: List<SubjectModelV2>;
  selectedMenuItem: string;
  handleCloseAccess: (subject: SubjectModelV2) => void;
  handleOpenAccess: (subject: SubjectModelV2) => void;
}

const SubjectsList = ({
  teacher,
  classroom,
  subjects,
  selectedMenuItem,
  handleCloseAccess,
  handleOpenAccess
}: SubjectsListProps) => {
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const schoolId = teacher.getSchool().getId();
  const isSchoolLicensed = teacher.getLicenseStatusForSchool(schoolId);
  const isTeacherLicensed = teacher.getMeta().isLicensed();

  const hiddenSubjects = useMemo(
    () => subjects.filter((subject) => subject.isHidden()),
    [subjects]
  );

  const sortedSubjects = useMemo(
    () => subjects.sortBy((subject) => subject.getName().toLowerCase()),
    [subjects]
  );

  const headingTooltipText =
    selectedMenuItem === 'Independent practice' ? (
      <p className='u-mar-b_0'>
        If a subject has open access, that means students can independently practice and view
        solutions for any question in that subject&apos;s Practice tab. Otherwise, students can only
        practice and view solutions from questions that you&apos;ve assigned to them.
        <br />
        <br />
        Close access by selecting a subject below. Open access to more subjects from our Subjects
        library.
      </p>
    ) : (
      <p className='u-mar-b_0'>Subjects practiced by your students.</p>
    );

  const hiddenSubjectsTooltipText = (
    <p className='u-mar-b_0'>
      Hidden subjects are no longer publicly available. Their guides are not accessible to teachers
      or students. However, assignments for these subjects will continue to work and data from these
      subjects will continue to appear in reports.
    </p>
  );

  return (
    <>
      <div className='teacher-subjects-list__subjects-container'>
        <Heading
          size='m'
          className='u-mar-b_3 u-display_flex u-justify-content_space-between u-align_items-center'
          style={{
            gridColumn: '1/-1'
          }}
        >
          <div>
            {selectedMenuItem === 'Independent practice'
              ? 'Independent practice'
              : 'All class subjects'}
            <WithTooltip content={headingTooltipText} placement='right' theme='light'>
              <Icon
                icon='question-circle'
                color='midnight-lightest'
                size='standalone'
                className='u-mar-l_2'
                iconStyle='regular'
              />
            </WithTooltip>
          </div>
          {hiddenSubjects.size > 0 && (
            <div>
              <Button
                variant={!showHidden ? 'text' : 'faded'}
                roundness='pill'
                size='s'
                color={!showHidden ? 'secondary' : 'primary'}
                onClick={() => setShowHidden(!showHidden)}
                className='u-font-weight_normal u-gap_1'
              >
                Show Hidden subjects
                <WithTooltip content={hiddenSubjectsTooltipText} placement='right' theme='light'>
                  <Icon icon='info-circle' iconStyle='regular' />
                </WithTooltip>
                {showHidden && <Icon icon='close' className='u-mar-l_1' />}
              </Button>
            </div>
          )}
        </Heading>
        {selectedMenuItem === 'Independent practice' && (
          <Banner className='u-mar-b_4'>
            <Banner.Icon icon='info' iconStyle='solid' />
            <span>
              Open access to more subjects from the&nbsp;
              <Anchor to='/subjects/high-school/test-prep'>Library</Anchor>
            </span>
          </Banner>
        )}
        <div className='teacher-subjects-list__subject-cards'>
          {sortedSubjects.map((subject) => {
            const isHidden = subject.isHidden();
            if (!showHidden && isHidden) {
              return null;
            }
            return (
              <SubjectsListCard
                key={subject.getId()}
                subject={subject}
                classroom={classroom}
                showActions={isTeacherLicensed && isSchoolLicensed}
                handleCloseAccess={handleCloseAccess}
                handleOpenAccess={handleOpenAccess}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

interface TeacherSubjectsListProps {
  params: {
    classId: string;
  };
}

const TeacherSubjectsList = ({params}: TeacherSubjectsListProps) => {
  const {classId} = params;

  const {
    isReady,
    resources: [teacher, classroom]
  } = useMandarkQueries([getTeacherQuery(), getClassroomQuery(classId)]);

  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('All class subjects');
  const [selectedSubject, setSelectedSubject] = useState<SubjectModelV2 | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  const findUnrestrictedSubjects = (classroomId: string): List<SubjectModelV2> =>
    classroom
      .getSubjects()
      .filter(
        (subject) => !subject.getClassroomRelationships(classroomId).getRestrictFreePractice()
      );

  const handleCloseAccess = useCallback(
    (subject: SubjectModelV2) => {
      setSelectedSubject(subject);
      setShowModal(true);
    },
    [setSelectedSubject, setShowModal]
  );

  const handleOpenAccess = useCallback(
    async (subject: SubjectModelV2) => {
      try {
        await setFreePracticeRestrictionForClassroom(classroom.getId(), subject.getId(), false);
        addToast({
          message: 'Your changes have been saved.',
          title: 'Success!',
          type: 'success'
        });
        getClassroomQuery(classId).invalidateInterest();
      } catch (error) {
        addToast({
          message:
            'We encountered an error while saving your changes. Please contact us if the problem persists.',
          title: 'Something went wrong',
          type: 'danger'
        });
      }
    },
    [classroom, classId]
  );

  if (!isReady) {
    return <LoadingIndicator />;
  }

  if (classroom.getSubjects().isEmpty()) {
    /**
     * If the user is masquerading, we take them to the top-level route of the Course Library (they are not allowed to see "Discover" results for another user).
     */
    return (
      <TrackedEmptyState
        title='No class subjects'
        body='No subjects are currently available for students to practice in this class. Visit the library to assign or open student practice.'
      />
    );
  }

  const classroomId = classroom.getId();
  const unrestrictedSubjects = findUnrestrictedSubjects(classroomId);
  const schoolId = teacher.getSchool().getId();
  const isSchoolLicensed = teacher.getLicenseStatusForSchool(schoolId);
  const isMobile = appStore.isMobileView();

  const triggerElement = (
    <Card className='teacher-subjects-list__mobile-dropdown u-mar-b_3' paddingLevel={2}>
      <Text>{selectedMenuItem}</Text>
      <Icon icon='caret-down' />
    </Card>
  );

  return (
    <div
      className={classnames('teacher-subjects-list', {
        'teacher-subjects-list--mobile': isMobile
      })}
    >
      {teacher.getMeta().isLicensed() &&
        isSchoolLicensed &&
        (isMobile ? (
          <MobileDropdown fillWrapper trigger={triggerElement}>
            <DropdownItem onClick={() => setSelectedMenuItem('All class subjects')}>
              All class subjects
            </DropdownItem>
            <DropdownItem onClick={() => setSelectedMenuItem('Independent practice')}>
              Independent practice
            </DropdownItem>
          </MobileDropdown>
        ) : (
          <Card className='teacher-subjects-list__menu-list-container u-mar-r_7 u-pad_2'>
            <MenuListItem
              text='All class subjects'
              onMenuItemClick={() => setSelectedMenuItem('All class subjects')}
              selected={selectedMenuItem === 'All class subjects'}
            />
            <MenuListItem
              text='Independent practice'
              onMenuItemClick={() => setSelectedMenuItem('Independent practice')}
              selected={selectedMenuItem === 'Independent practice'}
            />
          </Card>
        ))}
      {selectedMenuItem === 'Independent practice' && unrestrictedSubjects.size === 0 && (
        <EmptySubjectsList />
      )}
      {selectedMenuItem === 'Independent practice' && unrestrictedSubjects.size > 0 && (
        <SubjectsList
          subjects={unrestrictedSubjects}
          teacher={teacher}
          classroom={classroom}
          selectedMenuItem={selectedMenuItem}
          handleCloseAccess={handleCloseAccess}
          handleOpenAccess={handleOpenAccess}
        />
      )}
      {selectedMenuItem === 'All class subjects' && (
        <SubjectsList
          subjects={classroom.getSubjects()}
          teacher={teacher}
          classroom={classroom}
          selectedMenuItem={selectedMenuItem}
          handleCloseAccess={handleCloseAccess}
          handleOpenAccess={handleOpenAccess}
        />
      )}
      {showModal && (
        <RestrictFreePracticeModal
          classroomId={classroomId}
          fromClassroom
          handleClose={() => {
            setShowModal(false);
          }}
          onRestrict={async () => {
            try {
              await setFreePracticeRestrictionForClassroom(classId, selectedSubject.getId(), true);
              addToast({
                message: 'Your changes have been saved.',
                title: 'Success!',
                type: 'success'
              });
              getClassroomQuery(classId).invalidateInterest();
            } catch (error) {
              addToast({
                message:
                  'We encountered an error while saving your changes. Please contact us if the problem persists.',
                title: 'Something went wrong',
                type: 'danger'
              });
            }
          }}
          subject={selectedSubject}
        />
      )}
    </div>
  );
};

export default TeacherSubjectsList;
